import React, { lazy, ReactElement } from 'react'

import { useSearchParams } from 'react-router-dom'

import { Loader } from '../components/Loader'
import { isAffiliate } from '../services/api/affiliates'
import { exchangeToken, signInPassword, ssoInit } from '../services/api/auth'
import { clearSavedTokens, getRefreshToken, saveTokens } from '../services/authentication'
import { getRecaptchaToken } from '../services/recaptcha'
import { SignInOptions } from '../types/SignInOptions'
import { environmentVariables } from '../utils/environmentVariables'
import { redirectToUrl } from '../utils/location'
import { QueryParameters } from '../utils/queryParameters'

const LazySignInPage = lazy(() =>
  import('../pages/signin/SignInPage').then((module) => ({
    default: module.SignInPage,
  })),
)

export const SignInRoute = (): ReactElement => {
  const [searchParams] = useSearchParams()

  const signInPasswordSubmit = async ({
    isSso,
    username,
    password,
    authenticationCode,
    secondaryFactor,
  }: SignInOptions): Promise<void> => {
    const recaptchaToken = await getRecaptchaToken('login')
    if (isSso) {
      ssoInit(username, recaptchaToken)
    } else {
      const tokens = await signInPassword(username, password, recaptchaToken, authenticationCode, secondaryFactor)
      await saveTokensAndRedirect(tokens.access_token, tokens.refresh_token)
    }
  }

  const saveTokensAndRedirect = async (accessToken: string, refreshToken: string | null): Promise<void> => {
    saveTokens(accessToken, refreshToken)
    redirectToUrl(await redirectionAfterSignInUrl(accessToken))
  }

  const redirectionAfterSignInUrl = async (accessToken: string): Promise<string> => {
    const originParamUrlValue = originParamUrl()
    if (originParamUrlValue) {
      return originParamUrlValue
    } else if (await isAffiliate(accessToken)) {
      return environmentVariables('AFFILIATES_APP_URL')
    } else {
      return environmentVariables('EMPLOYEE_APP_URL')
    }
  }

  const originParamUrl = (): string | null => {
    const originParam = searchParams.get(QueryParameters.ORIGIN)
    try {
      if (originParam) {
        if (new URL(originParam).hostname.endsWith('swile.co')) {
          return originParam
        } else {
          console.error(`Trying to redirect to unauthorized url: '${originParam}'`)
        }
      }
    } catch (TypeError) {
      console.error(`Trying to redirect to invalid url: '${originParam}'`)
    }

    return null
  }

  // redirect with token from query parameters
  const accessTokenParam = searchParams.get(QueryParameters.ACCESS_TOKEN)
  if (accessTokenParam) {
    const refreshTokenParam = searchParams.get(QueryParameters.REFRESH_TOKEN)
    saveTokensAndRedirect(accessTokenParam, refreshTokenParam)
    return <Loader />
  }

  // authenticate with refresh token from cookie
  const refreshTokenCookie = getRefreshToken()
  if (refreshTokenCookie) {
    exchangeToken(refreshTokenCookie)
      .then((tokens) => saveTokensAndRedirect(tokens.access_token, tokens.refresh_token))
      .catch((error) => {
        // failure to exchange a refresh token mostly happens when a Swiler switches environment
        console.error(error)
        clearSavedTokens()
        redirectToUrl(window.location.href)
      })
    return <Loader />
  }

  // return sign in page if user wasn't previously redirected
  return <LazySignInPage onSubmit={signInPasswordSubmit} enableSso={true} />
}
